// src/components/mobile-app-download-button/mobile-app-download-button.tsx
import React2 from "react";
import { useQuery } from "@shared-ui/apollo-client-wrapper";
import { useBexApi } from "@shared-ui/bex-api-context";
import { IntersectionTracker } from "@shared-ui/clickstream-analytics-context";
import { Viewport, ViewSmall } from "@shared-ui/viewport-context";

// src/components/mobile-app-download-button-secondary-button/mobile-app-download-button-secondary-button.tsx
import React from "react";
import { useLocation } from "react-router-dom-v5-compat";
import { AnimatedIcon } from "@egds/react-app-growth";
import { EGDSButtonSize, EGDSSecondaryButton } from "@egds/react-core/button";
import { EGDSIcon } from "@egds/react-core/icons";
import { EGDSLayoutFlexItem } from "@egds/react-core/layout-flex";
import { EGDSText } from "@egds/react-core/text";
import { gql } from "@shared-ui/apollo-client-wrapper";
import { useClickTracker } from "@shared-ui/clickstream-analytics-context";
import { CustomerNotification, CustomerNotificationLocation } from "@shared-ui/customer-notifications";

// src/components/helpers/create-deep-link.ts
var createDeepLink = (linkUrl, canonicalUrl) => {
  try {
    const deepLinkUrl = new URL(linkUrl);
    if (!deepLinkUrl.searchParams.has("$canonical_url") && !deepLinkUrl.searchParams.has("deep_link_value") || !canonicalUrl) {
      return `${deepLinkUrl}/`;
    }
    return `${deepLinkUrl}${encodeURIComponent(canonicalUrl)}`;
  } catch (error) {
    return void 0;
  }
};

// src/components/mobile-app-download-button-secondary-button/mobile-app-download-button-secondary-button.tsx
var fragment = gql`
  fragment SecondaryButtonFragment on UISecondaryButton {
    __typename
    primary
    icon {
      id
      title
      description
    }
    analytics {
      linkName
      referrerId
      eventType
    }
  }
`;
var MobileAppDownloadButtonSecondaryButton = ({
  data,
  optionalPageId,
  bannerAction
}) => {
  var _a, _b;
  const {
    analytics,
    icon,
    primary
  } = data;
  const analyticsTracker = useClickTracker();
  const location = useLocation();
  const triggerRef = React.useRef(null);
  const ref = React.useRef(null);
  const [showBanner, setShowBanner] = React.useState(false);
  let branchLink;
  const handleOutsideClick = e => {
    if (ref.current && !ref.current.contains(e.target)) {
      if (showBanner) {
        setShowBanner(false);
      }
    }
  };
  const handleESCKeyPress = e => {
    if (e.key === "Escape") {
      if (showBanner) {
        setShowBanner(false);
      }
    }
  };
  React.useEffect(() => {
    document.addEventListener("keydown", handleESCKeyPress);
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("keydown", handleESCKeyPress);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  });
  if (!icon || !analytics) {
    return null;
  }
  const a11yLabel = !primary ? (_a = icon.title) != null ? _a : "" : "";
  if ((bannerAction == null ? void 0 : bannerAction.__typename) === "UIBannerLinkAction" && ((_b = bannerAction.resource) == null ? void 0 : _b.value)) {
    const canonicalUrl = `${location.pathname}${location.search}`;
    branchLink = createDeepLink(bannerAction.resource.value, canonicalUrl);
  }
  const onClickButton = () => {
    analyticsTracker(analytics.referrerId, analytics.linkName, void 0, []);
    setShowBanner(!showBanner);
  };
  return /* @__PURE__ */React.createElement(React.Fragment, null, (bannerAction == null ? void 0 : bannerAction.__typename) === "UIBannerLinkAction" && branchLink && /* @__PURE__ */React.createElement(EGDSLayoutFlexItem, null, /* @__PURE__ */React.createElement(EGDSSecondaryButton, {
    size: EGDSButtonSize.SMALL,
    tag: "a",
    href: branchLink,
    onClick: onClickButton
  }, /* @__PURE__ */React.createElement(AnimatedIcon, {
    name: icon.id,
    title: a11yLabel
  }), primary && /* @__PURE__ */React.createElement(EGDSText, {
    whiteSpace: "nowrap"
  }, primary))), (bannerAction == null ? void 0 : bannerAction.__typename) === "UINotificationAction" && /* @__PURE__ */React.createElement("div", {
    ref
  }, /* @__PURE__ */React.createElement(EGDSLayoutFlexItem, null, /* @__PURE__ */React.createElement(EGDSSecondaryButton, {
    size: EGDSButtonSize.SMALL,
    onClick: onClickButton,
    domRef: triggerRef
  }, /* @__PURE__ */React.createElement(EGDSIcon, {
    id: icon.id,
    name: icon.id,
    title: a11yLabel
  }), primary && /* @__PURE__ */React.createElement(EGDSText, {
    whiteSpace: "nowrap"
  }, primary))), showBanner && /* @__PURE__ */React.createElement(CustomerNotification, {
    inputs: {
      notificationLocation: CustomerNotificationLocation[bannerAction.notificationSlot],
      xPageId: optionalPageId
    }
  })));
};
MobileAppDownloadButtonSecondaryButton.fragment = fragment;
MobileAppDownloadButtonSecondaryButton.displayName = "MobileAppDownloadButtonSecondaryButton";

// src/components/mobile-app-download-button/query/mobile-app-download-button-query.tsx
import { gql as gql2 } from "@shared-ui/apollo-client-wrapper";
var MobileAppDownloadButtonRequestQuery = gql2`
  query MobileAppDownloadButtonQuery($context: ContextInput!, $optionalPageId: String) {
    notification(context: $context) {
      mobileAppDownloadButton(optionalPageId: $optionalPageId) {
        button {
          ...SecondaryButtonFragment
        }
        revealAction {
          event
          referrerId
          linkName
        }
        action {
          ... on UIBannerLinkAction {
            target
            resource {
              value
            }
          }
          ... on UINotificationAction {
            notificationSlot
          }
        }
      }
    }
  }
  ${MobileAppDownloadButtonSecondaryButton.fragment}
`;

// src/components/mobile-app-download-button/mobile-app-download-button.tsx
var __GetMobileAppDownloadButton = MobileAppDownloadButtonRequestQuery;
var MobileAppDownloadButton = props => {
  const {
    inputs,
    skipSsr
  } = props;
  const {
    context
  } = useBexApi();
  const variables = {
    ...inputs,
    context
  };
  const {
    data,
    error,
    loading
  } = useQuery(__GetMobileAppDownloadButton, {
    context: {
      shouldLogPerformance: true
    },
    variables,
    ssr: !skipSsr
  });
  if (loading || error || !data) {
    return null;
  }
  const {
    notification: {
      mobileAppDownloadButton: {
        button,
        revealAction,
        action
      }
    }
  } = data;
  if (!button || !button.icon || !button.analytics || !revealAction || !revealAction.linkName) {
    return null;
  }
  const pageId = inputs.optionalPageId || "";
  return /* @__PURE__ */React2.createElement(IntersectionTracker, {
    referrerId: revealAction.referrerId,
    linkName: revealAction.linkName,
    trackOnce: true
  }, /* @__PURE__ */React2.createElement(Viewport, null, /* @__PURE__ */React2.createElement(ViewSmall, null, /* @__PURE__ */React2.createElement(MobileAppDownloadButtonSecondaryButton, {
    data: button,
    optionalPageId: pageId,
    bannerAction: action
  }))));
};
MobileAppDownloadButton.displayName = "sharedui-MobileAppDownloadButton";
export { MobileAppDownloadButton };
/* istanbul ignore next -- @preserve */
//# sourceMappingURL=index.js.map